import { useState, type FormEvent } from "react";
import { CheckCircle } from "lucide-react";
import { z } from "zod";
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";
import { submitLead } from "@/lib/submitLead";
import LegalHub from "@/components/LegalHub";
import { WmtFormInput, WmtFormSubmitButton } from "@/components/ui/wmt-form-controls";
import { educationPrograms } from "@/data/education";
import MarketingConsent from "@/components/MarketingConsent";

const isEmail = (value: string) => z.string().email().safeParse(value).success;
const isTelegram = (value: string) => /^(?:@|https:\/\/t\.me\/)[a-zA-Z][a-zA-Z0-9_]{4,31}$/.test(value);
const schema = z.object({
  name: z.string().trim().min(1, "Укажите имя").max(100),
  company: z.string().trim().min(1, "Укажите компанию").max(200),
  position: z.string().trim().min(1, "Укажите должность").max(200),
  contact: z.string().trim().max(255).refine(value => isEmail(value) || isTelegram(value) || isValidPhoneNumber(value, "RU"), "Укажите email, телефон с кодом страны или Telegram в формате @username"),
  participants: z.coerce.number().int().min(1, "Укажите количество участников").max(100000, "Проверьте количество участников"),
  message: z.string().trim().min(1, "Расскажите о задаче обучения").max(1500, "Максимум 1500 символов"),
});

type Field = keyof z.infer<typeof schema>;
const initialForm = { name: "", company: "", position: "", contact: "", participants: "", message: "" };

export default function EducationContactSection({ program, onProgramChange }: { program: string; onProgramChange: (value: string) => void }) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState<Partial<Record<Field | "consent", string>>>({});
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const change = (field: Field, value: string) => {
    setForm(previous => ({ ...previous, [field]: value }));
    setErrors(previous => ({ ...previous, [field]: undefined }));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    const parsed = schema.safeParse(form);
    const nextErrors: typeof errors = {};
    if (!parsed.success) parsed.error.issues.forEach(issue => {
      const field = issue.path[0] as Field;
      nextErrors[field] ??= issue.message;
    });
    if (!consent) nextErrors.consent = "Необходимо согласие на обработку данных";
    setErrors(nextErrors);
    if (!parsed.success || !consent) {
      const first = Object.keys(nextErrors)[0];
      document.getElementById(`education-${first}`)?.focus();
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    const data = parsed.data;
    const email = isEmail(data.contact) ? data.contact : null;
    const telegram = isTelegram(data.contact) ? data.contact : null;
    const phone = !email && !telegram ? parsePhoneNumber(data.contact, "RU")?.number ?? data.contact : null;
    const programName = educationPrograms.find(item => item.id === program)?.title ?? (program === "courses" ? "Готовые онлайн-курсы" : "Подобрать программу");
    const source = "Страница обучения";
    const educationFields = {
      program: programName,
      company: data.company,
      position: data.position,
      participants: data.participants,
      contact: data.contact,
      request: data.message,
    };
    const message = [
      `Источник: ${source}`,
      `Обучение: ${educationFields.program}`,
      `Компания: ${educationFields.company}`,
      `Должность: ${educationFields.position}`,
      `Участников: ${educationFields.participants}`,
      `Контакт: ${educationFields.contact}`,
      educationFields.request,
    ].join("\n");
    try {
      await submitLead({
        form_type: "education",
        name: data.name, company: data.company, position: data.position, email, phone,
        contact_method: telegram ? telegram : email ? "email" : "phone",
        message,
        marketing_consent: marketingConsent,
      });
      setSubmitted(true);
    } catch {
      setSubmitError("Не удалось отправить заявку. Проверьте подключение и попробуйте ещё раз. Ваши данные сохранены в форме.");
    } finally {
      setSubmitting(false);
    }
  };

  const fields: { name: Exclude<Field, "message">; label: string; placeholder: string; autoComplete?: string; type?: string }[] = [
    { name: "name", label: "Имя", placeholder: "Как к вам обращаться", autoComplete: "name" },
    { name: "company", label: "Компания", placeholder: "Название компании", autoComplete: "organization" },
    { name: "position", label: "Должность", placeholder: "Ваша роль в компании", autoComplete: "organization-title" },
    { name: "contact", label: "Телефон, Telegram или email", placeholder: "+7, @username или email" },
    { name: "participants", label: "Количество участников", placeholder: "Например, 20", type: "number" },
  ];

  return <section id="contact" className="education-section education-contact education-section--rounded">
    <div className="education-container education-contact-layout">
      <div>
        <h2 className="education-heading education-contact-heading">Подберём формат <span className="text-accent">под вас</span></h2>
        <p className="education-contact-copy">Расскажите, кого нужно обучить, какие задачи автоматизировать и как вы планируете применять ИИ. Подберём практическую программу под ваш опыт, задачи и следующий шаг в AI-трансформации.</p>
      </div>
      <div className="education-form-shell">
        {submitted ? <div className="education-success" role="status">
          <CheckCircle size={48} />
          <h3>Спасибо за заявку</h3>
          <p>Свяжемся с вами и предложим подходящую программу.</p>
        </div> : <form onSubmit={submit} noValidate>
          <div className="education-form-grid">
            {fields.map(field => <div key={field.name}>
              <label htmlFor={`education-${field.name}`} className="education-label">{field.label}</label>
              <WmtFormInput id={`education-${field.name}`} name={field.name} type={field.type ?? "text"} autoComplete={field.autoComplete} placeholder={field.placeholder} value={form[field.name]} onChange={event => change(field.name, event.target.value)} error={errors[field.name]} required min={field.type === "number" ? 1 : undefined} max={field.type === "number" ? 100000 : undefined} step={field.type === "number" ? 1 : undefined} maxLength={field.name === "contact" ? 255 : 200} />
            </div>)}
            <div>
              <label htmlFor="education-program" className="education-label">Формат обучения</label>
              <select id="education-program" value={program} onChange={event => onProgramChange(event.target.value)} className="education-select">
                <option value="">Нужна помощь с выбором</option>
                {educationPrograms.map(item => <option key={item.id} value={item.id}>{item.title}</option>)}
                <option value="courses">Готовые онлайн-курсы</option>
              </select>
            </div>
            <div className="education-form-wide">
            <label htmlFor="education-message" className="education-label">Что хотите изменить с помощью обучения</label>
            <textarea id="education-message" value={form.message} onChange={event => change("message", event.target.value)} rows={4} maxLength={1500} required aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? "education-message-error" : undefined} placeholder="Задачи команды, опыт работы с ИИ, желаемый результат" className="education-select resize-y" />
            {errors.message && <p id="education-message-error" className="text-xs text-destructive mt-1">{errors.message}</p>}
            </div>
          </div>
          <div className="education-consent">
            <input id="education-consent" type="checkbox" checked={consent} onChange={event => { setConsent(event.target.checked); setErrors(previous => ({ ...previous, consent: undefined })); }} aria-invalid={Boolean(errors.consent)} />
            <div><label htmlFor="education-consent">Я даю согласие на обработку персональных данных на условиях </label><button type="button" onClick={() => setLegalOpen(true)}>отдельного согласия</button>.</div>
          </div>
          {errors.consent && <p className="education-form-error">{errors.consent}</p>}
          <MarketingConsent id="education-marketing-consent" checked={marketingConsent} onChange={setMarketingConsent} className="education-consent" labelClassName="text-left" />
          {submitError && <p role="alert" className="education-form-error">{submitError}</p>}
          <WmtFormSubmitButton submitting={submitting} className="education-submit"><span className="optical-center">Обсудить обучение</span></WmtFormSubmitButton>
        </form>}
      </div>
    </div>
    <LegalHub open={legalOpen} onOpenChange={setLegalOpen} defaultSection="confidentiality" />
  </section>;
}
